"use client";
import AuthorTag from "@/components/custom/author-tag";
import CategoryHeading from "@/components/custom/category-heading";
import { Skeleton } from "@/components/ui/skeleton";
import { getValueByDevice } from "@/lib/image-helper";
import { articlePath, formatRelativeTime, imagePath } from "@/lib/userUtils";
import { RelatedArticlesAction } from "@/server-actions/article-actions";
import findDeviceInfo from "@/server-actions/get-device-info";
import { ArticlesForCardQuery } from "@/types/generated";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect } from "react";
import BookmarkTag from "../bookmark";

type Props = {
  categories: string[];
  tags: string[];
  device: "mobile" | "desktop";
  excludeIds: string;
};

const RelatedArticles = ({ device, tags, excludeIds, categories }: Props) => {
  const [loading, setLoading] = React.useState(true);
  const [articles, setArticles] = React.useState<
    ArticlesForCardQuery["articles"] | null
  >(null);

  const loadMoreArticles = React.useCallback(
    async (categories: string[], tags: string[], excludeIds: string) => {
      setLoading(true);
      const res = await RelatedArticlesAction(categories, tags, excludeIds);

      if (res?.success) {
        const newArticles = res?.data ?? [];
        setArticles(newArticles);

        setLoading(false);
      } else {
        // Handle error here
        console.error(res?.message);
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadMoreArticles(categories, tags, excludeIds);
  }, [loadMoreArticles, categories, tags]);

  return (
    <>
      <div className="container mx-auto max-w-3xl ">
        <CategoryHeading category="Related Articles" />
      </div>
      <div className="container flex flex-col mx-auto max-w-3xl rounded-md items-start justify-center gap-4">
        {loading &&
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={`sk-${i}`} className="w-full h-[120px] rounded-md" />
          ))}
        {!loading &&
          articles?.map((article, i) => (
            <div
              className="flex w-full group border border-border rounded-sm lg:p-4"
              key={article?.documentId}
            >
              <div className="grid grid-cols-12 gap-4 w-full">
                <div className="col-span-4 lg:col-span-2 w-full">
                  <div className="relative w-full h-[120px] lg:h-[100px] overflow-hidden rounded-s-sm lg:rounded-sm">
                    <Head>
                      <link
                        rel="preload"
                        href={imagePath(article?.featuredImage?.url)}
                        as="image"
                        fetchPriority="high"
                      />
                    </Head>
                    <Image
                      src={imagePath(article?.featuredImage?.url)}
                      width={getValueByDevice(device, [250, 250])}
                      height={getValueByDevice(device, [250, 250])}
                      quality={100}
                      className="absolute rounded-s-sm lg:rounded-sm  inset-0 w-full h-full object-cover transition-all duration-300 transform-gpu group-hover:scale-105 dark:brightness-75 dark:group-hover:brightness-100"
                      alt="image"
                      priority
                    />
                  </div>
                </div>
                <div className="col-span-8 lg:col-span-10 flex flex-col justify-center w-full gap-2">
                  <h2 className=" text-base line-clamp-2 dark:text-neutral-300 font-semibold">
                    <Link
                      href={articlePath(article)}
                      className="hover:underline"
                    >
                      {article?.title}
                    </Link>
                  </h2>
                  <p className="hidden lg:block text-sm text-neutral-500 dark:text-neutral-400 line-clamp-2">
                    {article?.description}
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">
                    <AuthorTag article={article} />
                    <span className="text-xs text-neutral-600 dark:text-neutral-400">
                      {formatRelativeTime(article?.articlePublishedAt)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        {!loading && articles?.length === 0 && (
          <div className="flex flex-col items-center justify-center w-full h-full py-10 text-center text-neutral-500">
            <h2 className="text-lg font-semibold">No related articles found</h2>
          </div>
        )}
      </div>
    </>
  );
};

export default RelatedArticles;
